import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Autocomplete,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import { useCreateQuotationMutation, useUpdateQuotationMutation, useGetQuotationQuery } from '../../../store/api/quotationsApi';
import { useGetCustomersQuery } from '../../../store/api/salesApi';
import { useLazyGetProductsQuery } from '../../../store/api/productsApi';
import { useGetLocationsQuery } from '../../../store/api/inventoryApi';
import { useGetUsersQuery } from '../../../store/api/userApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';

const DEFAULT_VAT = 5;

function QuotationForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id && id !== 'new';

  const { data: quotation, isLoading: loadingQ } = useGetQuotationQuery(id, { skip: !isEdit });
  const [createQuotation] = useCreateQuotationMutation();
  const [updateQuotation] = useUpdateQuotationMutation();
  const { data: customers } = useGetCustomersQuery();
  const [triggerProductSearch, { data: productSearchData, isFetching: searchingProducts }] = useLazyGetProductsQuery();
  const { data: company } = useGetCurrentCompanyQuery();
  const { data: locations } = useGetLocationsQuery();
  const { data: usersData } = useGetUsersQuery();
  const userList = Array.isArray(usersData) ? usersData : usersData?.users ?? [];

  const currency = company?.currency || 'USD';
  const vatIncluded = company?.salesVatIncluded !== false;
  const defaultVat = Number(company?.vatRate) || DEFAULT_VAT;

  const [formData, setFormData] = useState({
    customerId: '',
    quotationDate: new Date().toISOString().split('T')[0],
    validUntil: '',
    notes: '',
    salesPersonId: '',
    locationId: '',
  });
  const [items, setItems] = useState([]);
  const [productSearch, setProductSearch] = useState('');

  useEffect(() => {
    if (quotation && isEdit) {
      setFormData({
        customerId: quotation.customerId || '',
        quotationDate: quotation.quotationDate ? quotation.quotationDate.substring(0, 10) : '',
        validUntil: quotation.validUntil ? quotation.validUntil.substring(0, 10) : '',
        notes: quotation.notes || '',
        salesPersonId: quotation.salesPersonId || '',
        locationId: quotation.locationId || '',
      });
      setItems(
        (quotation.items || []).map((it) => ({
          productId: it.productId,
          productName: it.product?.name,
          quantity: it.quantity,
          unit: it.unit || 'pcs',
          unitPrice: it.unitPrice,
          discount: Number(it.discount) || 0,
          vatPercent: Number(it.vatPercent) || defaultVat,
        }))
      );
    }
  }, [quotation, isEdit]);

  const toNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

  const computeLine = (item) => {
    const qty = toNum(item.quantity);
    const price = toNum(item.unitPrice);
    const discount = toNum(item.discount);
    const vatPct = toNum(item.vatPercent);
    const lineSubtotal = Math.max(0, qty * price - discount);
    let vatAmount = 0;
    if (vatPct > 0) {
      vatAmount = vatIncluded ? lineSubtotal - lineSubtotal / (1 + vatPct / 100) : lineSubtotal * (vatPct / 100);
    }
    const lineTotal = vatIncluded ? lineSubtotal : lineSubtotal + vatAmount;
    return { vatAmount: Math.round(vatAmount * 100) / 100, lineTotal: Math.round(lineTotal * 100) / 100 };
  };

  const orderVatTotal = items.reduce((sum, item) => sum + computeLine(item).vatAmount, 0);
  const orderSubtotal = items.reduce((sum, item) => sum + toNum(item.quantity) * toNum(item.unitPrice), 0);
  const orderDiscountTotal = items.reduce((sum, item) => sum + toNum(item.discount), 0);
  const netOrder = orderSubtotal - orderDiscountTotal;
  const grandTotal = vatIncluded ? netOrder : netOrder + orderVatTotal;

  const handleAddProduct = (product) => {
    if (!product) return;
    setItems((prev) => [
      ...prev,
      {
        productId: product.id,
        productName: product.name,
        quantity: 1,
        unit: 'pcs',
        unitPrice: product.sellingPrice ?? 0,
        discount: 0,
        vatPercent: defaultVat,
      },
    ]);
    setProductSearch('');
  };

  const handleRemoveItem = (index) => setItems((prev) => prev.filter((_, i) => i !== index));

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customerId) {
      alert('Customer is required');
      return;
    }
    if (!formData.quotationDate) {
      alert('Quotation date is required');
      return;
    }
    if (items.length === 0 || items.some((i) => !i.productId)) {
      alert('Add at least one item');
      return;
    }

    const payload = {
      ...formData,
      quotationDate: formData.quotationDate,
      validUntil: formData.validUntil || undefined,
      items: items.map((item) => {
        const { vatAmount, lineTotal } = computeLine(item);
        return {
          productId: item.productId,
          quantity: parseFloat(item.quantity),
          unit: item.unit || undefined,
          unitPrice: parseFloat(item.unitPrice),
          discount: toNum(item.discount),
          vatPercent: toNum(item.vatPercent) || defaultVat,
          vatAmount,
          total: lineTotal,
        };
      }),
    };

    try {
      if (isEdit) {
        await updateQuotation({ id, ...payload }).unwrap();
        alert('Quotation updated');
        navigate(`/quotations/${id}`);
      } else {
        const created = await createQuotation(payload).unwrap();
        alert('Quotation created');
        navigate(`/quotations/${created.id}`);
      }
    } catch (err) {
      alert(err?.data?.message || 'Error saving quotation');
    }
  };

  if (isEdit && loadingQ) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(isEdit ? `/quotations/${id}` : '/quotations')}>
          Back
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          {isEdit ? 'Edit Quotation' : 'New Quotation'}
        </Typography>
      </Box>

      <Paper component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              select
              label="Customer *"
              value={formData.customerId}
              onChange={(e) => setFormData({ ...formData, customerId: e.target.value })}
              required
            >
              <MenuItem value="">Select</MenuItem>
              {(customers || []).map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              type="date"
              label="Quotation Date *"
              value={formData.quotationDate}
              onChange={(e) => setFormData({ ...formData, quotationDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              type="date"
              label="Valid Until"
              value={formData.validUntil}
              onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              select
              label="Sales Person"
              value={formData.salesPersonId}
              onChange={(e) => setFormData({ ...formData, salesPersonId: e.target.value })}
            >
              <MenuItem value="">None</MenuItem>
              {userList.map((u) => (
                <MenuItem key={u.id} value={u.id}>{u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.email}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              select
              label="Location"
              value={formData.locationId}
              onChange={(e) => setFormData({ ...formData, locationId: e.target.value })}
            >
              <MenuItem value="">None</MenuItem>
              {(locations || []).map((loc) => (
                <MenuItem key={loc.id} value={loc.id}>{loc.name} ({loc.code})</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              multiline
              rows={2}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Items</Typography>
            <Autocomplete
              sx={{ mb: 2, maxWidth: 400 }}
              options={productSearchData?.products || []}
              loading={searchingProducts}
              value={null}
              inputValue={productSearch}
              getOptionLabel={(opt) => opt?.name || ''}
              onInputChange={(_, v) => {
                setProductSearch(v);
                if (v && v.length >= 2) triggerProductSearch({ search: v, limit: 20 });
              }}
              onChange={(_, v) => handleAddProduct(v)}
              renderInput={(params) => <TextField {...params} label="Search product" placeholder="Type to search" />}
            />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Qty</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Unit Price</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Discount</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>VAT %</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item, idx) => {
                    const { lineTotal } = computeLine(item);
                    return (
                      <TableRow key={idx}>
                        <TableCell>{item.productName || '-'}</TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                const current = Number(item.quantity || 0) || 0;
                                const next = Math.max(0.01, current - 1);
                                handleItemChange(idx, 'quantity', String(next));
                              }}
                              title="Decrease"
                            >
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                            <TextField
                              type="number"
                              size="small"
                              value={item.quantity}
                              onChange={(e) => handleItemChange(idx, 'quantity', e.target.value)}
                              sx={{ width: 80 }}
                              inputProps={{ min: 0.01, step: 0.01 }}
                              required
                            />
                            <IconButton
                              size="small"
                              onClick={() => {
                                const current = Number(item.quantity || 0) || 0;
                                const next = Math.max(0.01, current + 1);
                                handleItemChange(idx, 'quantity', String(next));
                              }}
                              title="Increase"
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            value={item.unitPrice}
                            onChange={(e) => handleItemChange(idx, 'unitPrice', e.target.value)}
                            inputProps={{ min: 0, step: 0.01 }}
                            sx={{ width: 100 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            value={item.discount}
                            onChange={(e) => handleItemChange(idx, 'discount', e.target.value)}
                            inputProps={{ min: 0, step: 0.01 }}
                            sx={{ width: 80 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            value={item.vatPercent}
                            onChange={(e) => handleItemChange(idx, 'vatPercent', e.target.value)}
                            inputProps={{ min: 0, step: 0.01 }}
                            sx={{ width: 70 }}
                          />
                        </TableCell>
                        <TableCell>{new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(lineTotal)}</TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => handleRemoveItem(idx)}><DeleteIcon fontSize="small" /></IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="h6" sx={{ mt: 2 }}>Grand Total: {new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(grandTotal)}</Typography>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Button type="submit" variant="contained" startIcon={<SaveIcon />} sx={{ borderRadius: 2 }}>
              {isEdit ? 'Update Quotation' : 'Create Quotation'}
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}

export default QuotationForm;
